import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ClipboardCheck, Calendar, Check, X, Clock, CheckCircle, XCircle, Plus, Trash2, Ban, FileText } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import Navigation from '../components/Navigation';
import { getAdminHeaders, isFullAdmin } from '../lib/auth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pendente: { label: 'Pendente', icon: Clock, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  confirmado: { label: 'Confirmado', icon: CheckCircle, color: 'bg-green-100 text-green-700 border-green-200' },
  recusado: { label: 'Recusado', icon: XCircle, color: 'bg-red-100 text-red-700 border-red-200' },
};

const EscalaTrabalho = () => {
  const navigate = useNavigate();
  const [myAreas, setMyAreas] = useState([]);
  const [areaLabels, setAreaLabels] = useState({});
  const [volunteerId, setVolunteerId] = useState('');
  const [volunteerName, setVolunteerName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [liderAreas, setLiderAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState('');
  const [escalas, setEscalas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  // Multi-select state
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedAreasForRequest, setSelectedAreasForRequest] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Block dates state
  const [blockDates, setBlockDates] = useState([]);
  const [newBlockDate, setNewBlockDate] = useState('');
  const [blockJustificativa, setBlockJustificativa] = useState('');
  const [submittingBlock, setSubmittingBlock] = useState(false);
  const [indisponibilidades, setIndisponibilidades] = useState([]);
  const [activeTab, setActiveTab] = useState('disponibilidade');
  // Admin volunteer selector
  const [allVolunteers, setAllVolunteers] = useState([]);
  const [selectedVolunteerForAdmin, setSelectedVolunteerForAdmin] = useState('');

  const canShowForms = volunteerId || isAdmin;

  useEffect(() => { fetchMyAreas(); }, []);

  const fetchMyAreas = async () => {
    try {
      const res = await axios.get(`${API}/escalas/my-areas`, { headers: getAdminHeaders() });
      const data = res.data;
      const areas = data.is_admin
        ? Object.keys(data.area_labels)
        : data.areas;
      setMyAreas(areas);
      setAreaLabels(data.area_labels);
      setVolunteerId(data.volunteer_id);
      setVolunteerName(data.volunteer_name);
      setIsAdmin(data.is_admin);
      setLiderAreas(data.lider_areas || []);
      if (areas.length > 0) setSelectedArea(areas[0]);

      // Admin without volunteer record: load volunteer list
      if (data.is_admin && !data.volunteer_id) {
        const volRes = await axios.get(`${API}/volunteers`, { headers: getAdminHeaders() });
        const vols = Array.isArray(volRes.data) ? volRes.data : (volRes.data.volunteers || []);
        setAllVolunteers(vols);
      }
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar áreas');
    } finally {
      setLoading(false);
    }
  };

  const fetchEscalas = useCallback(async () => {
    if (!selectedArea) return;
    try {
      const params = new URLSearchParams({ area: selectedArea });
      if (filterStatus) params.append('status', filterStatus);
      const res = await axios.get(`${API}/escalas?${params}`, { headers: getAdminHeaders() });
      setEscalas(res.data.escalas || []);
    } catch (e) { console.error(e); }
  }, [selectedArea, filterStatus]);

  useEffect(() => { fetchEscalas(); }, [fetchEscalas]);

  const handleRequest = async () => {
    if (selectedDates.length === 0) { toast.error('Selecione pelo menos uma data'); return; }
    if (selectedAreasForRequest.length === 0) { toast.error('Selecione pelo menos uma área'); return; }
    if (isAdmin && !volunteerId && !selectedVolunteerForAdmin) { toast.error('Selecione um voluntário'); return; }
    setSubmitting(true);
    try {
      const payload = {
        areas: selectedAreasForRequest,
        datas: selectedDates,
      };
      if (isAdmin && !volunteerId && selectedVolunteerForAdmin) {
        payload.volunteer_id = selectedVolunteerForAdmin;
      }
      const res = await axios.post(`${API}/escalas/batch`, payload, { headers: getAdminHeaders() });
      toast.success(res.data.message);
      setSelectedDates([]);
      setSelectedAreasForRequest([]);
      setNewDate('');
      fetchEscalas();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao solicitar'); }
    finally { setSubmitting(false); }
  };

  const addDate = () => {
    if (!newDate) { toast.error('Selecione uma data'); return; }
    if (selectedDates.includes(newDate)) { toast.error('Data já adicionada'); return; }
    setSelectedDates(prev => [...prev, newDate].sort());
    setNewDate('');
  };

  const removeDate = (d) => setSelectedDates(prev => prev.filter(x => x !== d));

  const toggleAreaForRequest = (area) => {
    setSelectedAreasForRequest(prev =>
      prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]
    );
  };

  // Block dates functions
  const addBlockDate = () => {
    if (!newBlockDate) { toast.error('Selecione uma data'); return; }
    if (blockDates.includes(newBlockDate)) { toast.error('Data já adicionada'); return; }
    setBlockDates(prev => [...prev, newBlockDate].sort());
    setNewBlockDate('');
  };

  const removeBlockDate = (d) => setBlockDates(prev => prev.filter(x => x !== d));

  const handleBlockSubmit = async () => {
    if (blockDates.length === 0) { toast.error('Selecione pelo menos uma data'); return; }
    if (isAdmin && !volunteerId && !selectedVolunteerForAdmin) { toast.error('Selecione um voluntário'); return; }
    setSubmittingBlock(true);
    try {
      const payload = {
        datas: blockDates,
        justificativa: blockJustificativa,
      };
      if (isAdmin && !volunteerId && selectedVolunteerForAdmin) {
        payload.volunteer_id = selectedVolunteerForAdmin;
      }
      const res = await axios.post(`${API}/escalas/indisponibilidades`, payload, { headers: getAdminHeaders() });
      toast.success(res.data.message);
      setBlockDates([]);
      setBlockJustificativa('');
      fetchIndisponibilidades();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao bloquear'); }
    finally { setSubmittingBlock(false); }
  };

  const fetchIndisponibilidades = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/escalas/indisponibilidades`, { headers: getAdminHeaders() });
      setIndisponibilidades(res.data.indisponibilidades || []);
    } catch (e) { console.error(e); }
  }, []);

  const handleRemoveBlock = async (id) => {
    try {
      await axios.delete(`${API}/escalas/indisponibilidades/${id}`, { headers: getAdminHeaders() });
      toast.success('Indisponibilidade removida');
      fetchIndisponibilidades();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  useEffect(() => { if (volunteerId || isAdmin) fetchIndisponibilidades(); }, [volunteerId, isAdmin, fetchIndisponibilidades]);

  const handleApprove = async (id, status) => {
    try {
      await axios.put(`${API}/escalas/${id}/approve`, { status }, { headers: getAdminHeaders() });
      toast.success(status === 'confirmado' ? 'Escala confirmada!' : 'Escala recusada');
      fetchEscalas();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const handleCancel = async (id) => {
    try {
      await axios.delete(`${API}/escalas/${id}`, { headers: getAdminHeaders() });
      toast.success('Escala cancelada');
      fetchEscalas();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const formatDate = (d) => {
    try { return new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' }); }
    catch { return d; }
  };

  // Group escalas by date
  const groupedByDate = {};
  escalas.forEach(e => {
    if (!groupedByDate[e.data]) groupedByDate[e.data] = [];
    groupedByDate[e.data].push(e);
  });
  const sortedDates = Object.keys(groupedByDate).sort();

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-7 h-7" />
            <h1 className="font-nunito font-bold text-3xl">Escala de Trabalho</h1>
          </div>
          <p className="font-inter text-white/70 text-sm mt-1">Escolha suas datas para servir e aguarde a aprovacao do lider</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {loading ? (
          <p className="text-gray-500 text-center py-8">Carregando...</p>
        ) : myAreas.length === 0 ? (
          <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
            <ClipboardCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="font-inter text-gray-500">Voce nao esta vinculado a nenhuma area.</p>
            <p className="font-inter text-gray-400 text-sm mt-1">Peca ao administrador para vincular seu cadastro de voluntario.</p>
          </Card>
        ) : (
          <>
            {/* Area Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              {myAreas.map(area => (
                <button
                  key={area}
                  data-testid={`area-tab-${area}`}
                  onClick={() => setSelectedArea(area)}
                  className={`px-4 py-2 rounded-full text-sm font-inter font-semibold transition-all whitespace-nowrap ${
                    selectedArea === area
                      ? 'bg-[#1A4D2E] text-white'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {areaLabels[area] || area}
                </button>
              ))}
            </div>

            {/* Action Tabs: Disponibilidade / Indisponibilidade */}
            {canShowForms && (
              <div className="flex gap-2">
                <button
                  data-testid="tab-disponibilidade"
                  onClick={() => setActiveTab('disponibilidade')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-inter font-semibold transition-all ${
                    activeTab === 'disponibilidade'
                      ? 'bg-[#1A4D2E] text-white shadow-md'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Plus className="w-4 h-4" /> Informar Disponibilidade
                </button>
                <button
                  data-testid="tab-indisponibilidade"
                  onClick={() => setActiveTab('indisponibilidade')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-inter font-semibold transition-all ${
                    activeTab === 'indisponibilidade'
                      ? 'bg-red-600 text-white shadow-md'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Ban className="w-4 h-4" /> Informar Indisponibilidade
                </button>
              </div>
            )}

            {/* Disponibilidade Form */}
            {canShowForms && activeTab === 'disponibilidade' && (
              <Card className="bg-white rounded-2xl p-5 shadow-md border border-[#E1ECE5]">
                <h3 className="font-nunito font-bold text-[#1A4D2E] mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5" /> Solicitar Datas para Servir
                </h3>

                {/* Admin volunteer selector */}
                {isAdmin && !volunteerId && allVolunteers.length > 0 && (
                  <div className="mb-4">
                    <label className="font-inter text-sm font-semibold text-gray-600 mb-2 block">Voluntario</label>
                    <select
                      data-testid="select-volunteer-disponibilidade"
                      value={selectedVolunteerForAdmin}
                      onChange={e => {
                        setSelectedVolunteerForAdmin(e.target.value);
                        setSelectedAreasForRequest([]);
                      }}
                      className="w-full rounded-xl border border-gray-200 h-10 px-3 text-sm bg-white"
                    >
                      <option value="">Selecione um voluntário...</option>
                      {allVolunteers.map(v => (
                        <option key={v.id} value={v.id}>{v.nome} ({(v.areas || []).join(', ')})</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Areas Checkboxes */}
                <div className="mb-4">
                  <label className="font-inter text-sm font-semibold text-gray-600 mb-2 block">Areas</label>
                  <div className="flex flex-wrap gap-2">
                    {(isAdmin && !volunteerId && selectedVolunteerForAdmin
                      ? (allVolunteers.find(v => v.id === selectedVolunteerForAdmin)?.areas || [])
                      : myAreas
                    ).map(area => (
                      <button
                        key={area}
                        data-testid={`area-check-${area}`}
                        onClick={() => toggleAreaForRequest(area)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-inter font-medium transition-all border ${
                          selectedAreasForRequest.includes(area)
                            ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-[#1A4D2E]/40'
                        }`}
                      >
                        {selectedAreasForRequest.includes(area) && <Check className="w-3.5 h-3.5" />}
                        {areaLabels[area] || area}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Multi-date Picker */}
                <div className="mb-4">
                  <label className="font-inter text-sm font-semibold text-gray-600 mb-2 block">Datas</label>
                  <div className="flex items-end gap-2 flex-wrap">
                    <input
                      type="date"
                      data-testid="input-escala-date"
                      value={newDate}
                      onChange={e => setNewDate(e.target.value)}
                      className="rounded-xl border border-gray-200 h-10 px-3 text-sm min-w-[180px]"
                    />
                    <Button
                      data-testid="btn-add-date"
                      onClick={addDate}
                      variant="outline"
                      className="rounded-full h-10 px-4 border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E]/5"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Adicionar Data
                    </Button>
                  </div>
                  {selectedDates.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {selectedDates.map(d => (
                        <span key={d} className="inline-flex items-center gap-1 bg-[#E8F5ED] text-[#1A4D2E] text-sm font-inter font-medium px-3 py-1 rounded-full">
                          {formatDate(d)}
                          <button
                            data-testid={`remove-date-${d}`}
                            onClick={() => removeDate(d)}
                            className="hover:text-red-600 transition-colors ml-0.5"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Summary & Submit */}
                <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-gray-100">
                  <p className="font-inter text-xs text-gray-400">
                    {selectedAreasForRequest.length} área(s) x {selectedDates.length} data(s) = <strong>{selectedAreasForRequest.length * selectedDates.length}</strong> solicitação(ões) | Voluntário: <strong>{
                      isAdmin && !volunteerId && selectedVolunteerForAdmin
                        ? (allVolunteers.find(v => v.id === selectedVolunteerForAdmin)?.nome || 'Selecione')
                        : volunteerName
                    }</strong>
                  </p>
                  <Button
                    data-testid="btn-request-escala"
                    onClick={handleRequest}
                    disabled={submitting || selectedDates.length === 0 || selectedAreasForRequest.length === 0}
                    className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full px-6 h-10 disabled:opacity-50"
                  >
                    <Calendar className="w-4 h-4 mr-2" /> {submitting ? 'Enviando...' : 'Solicitar'}
                  </Button>
                </div>
              </Card>
            )}

            {/* Indisponibilidade Form */}
            {canShowForms && activeTab === 'indisponibilidade' && (
              <Card className="bg-white rounded-2xl p-5 shadow-md border border-red-100">
                <h3 className="font-nunito font-bold text-red-700 mb-4 flex items-center gap-2">
                  <Ban className="w-5 h-5" /> Informar Datas Indisponiveis
                </h3>
                <p className="font-inter text-xs text-gray-500 mb-4">Informe as datas em que voce <strong>nao podera</strong> servir. O lider sera notificado.</p>

                {/* Admin volunteer selector */}
                {isAdmin && !volunteerId && allVolunteers.length > 0 && (
                  <div className="mb-4">
                    <label className="font-inter text-sm font-semibold text-gray-600 mb-2 block">Voluntario</label>
                    <select
                      data-testid="select-volunteer-indisponibilidade"
                      value={selectedVolunteerForAdmin}
                      onChange={e => setSelectedVolunteerForAdmin(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 h-10 px-3 text-sm bg-white"
                    >
                      <option value="">Selecione um voluntário...</option>
                      {allVolunteers.map(v => (
                        <option key={v.id} value={v.id}>{v.nome}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Multi-date Picker for blocking */}
                <div className="mb-4">
                  <label className="font-inter text-sm font-semibold text-gray-600 mb-2 block">Datas Indisponiveis</label>
                  <div className="flex items-end gap-2 flex-wrap">
                    <input
                      type="date"
                      data-testid="input-block-date"
                      value={newBlockDate}
                      onChange={e => setNewBlockDate(e.target.value)}
                      className="rounded-xl border border-gray-200 h-10 px-3 text-sm min-w-[180px]"
                    />
                    <Button
                      data-testid="btn-add-block-date"
                      onClick={addBlockDate}
                      variant="outline"
                      className="rounded-full h-10 px-4 border-red-400 text-red-600 hover:bg-red-50"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Adicionar Data
                    </Button>
                  </div>
                  {blockDates.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {blockDates.map(d => (
                        <span key={d} className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-sm font-inter font-medium px-3 py-1 rounded-full border border-red-200">
                          {formatDate(d)}
                          <button
                            data-testid={`remove-block-${d}`}
                            onClick={() => removeBlockDate(d)}
                            className="hover:text-red-900 transition-colors ml-0.5"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Justificativa */}
                <div className="mb-4">
                  <label className="font-inter text-sm font-semibold text-gray-600 mb-2 block">
                    Justificativa <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    data-testid="input-block-justificativa"
                    value={blockJustificativa}
                    onChange={e => setBlockJustificativa(e.target.value)}
                    placeholder="Ex: Viagem, compromisso pessoal..."
                    className="w-full rounded-xl border border-gray-200 h-10 px-3 text-sm"
                  />
                </div>

                {/* Submit */}
                <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-gray-100">
                  <p className="font-inter text-xs text-gray-400">
                    {blockDates.length} data(s) a bloquear | Voluntario: <strong>{
                      isAdmin && !volunteerId && selectedVolunteerForAdmin
                        ? (allVolunteers.find(v => v.id === selectedVolunteerForAdmin)?.nome || 'Selecione')
                        : volunteerName
                    }</strong>
                  </p>
                  <Button
                    data-testid="btn-submit-block"
                    onClick={handleBlockSubmit}
                    disabled={submittingBlock || blockDates.length === 0}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6 h-10 disabled:opacity-50"
                  >
                    <Ban className="w-4 h-4 mr-2" /> {submittingBlock ? 'Enviando...' : 'Bloquear Datas'}
                  </Button>
                </div>
              </Card>
            )}

            {/* Blocked Dates List */}
            {indisponibilidades.length > 0 && (
              <Card className="bg-white rounded-2xl shadow-sm border border-red-100 overflow-hidden">
                <div className="bg-red-50 px-4 py-2.5 border-b border-red-100">
                  <p className="font-nunito font-bold text-red-700 flex items-center gap-2 text-sm">
                    <Ban className="w-4 h-4" /> Datas Bloqueadas ({indisponibilidades.length})
                  </p>
                </div>
                <div className="divide-y divide-gray-100">
                  {indisponibilidades.map(item => {
                    const isMine = item.volunteer_id === volunteerId;
                    return (
                      <div key={item.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50/50 transition-colors">
                        <div className="flex items-center gap-3">
                          <Ban className="w-4 h-4 text-red-400" />
                          <div>
                            <p className="font-inter font-semibold text-gray-800 text-sm">
                              {formatDate(item.data)}
                              {!isMine && <span className="text-gray-500 font-normal ml-2">- {item.volunteer_name}</span>}
                              {isMine && <span className="text-xs text-red-500 ml-1">(voce)</span>}
                            </p>
                            {item.justificativa && (
                              <p className="font-inter text-xs text-gray-400 flex items-center gap-1">
                                <FileText className="w-3 h-3" /> {item.justificativa}
                              </p>
                            )}
                          </div>
                        </div>
                        {(isMine || isAdmin || isFullAdmin()) && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                data-testid={`remove-indisponibilidade-${item.id}`}
                                className="text-gray-400 hover:text-red-500 rounded-full p-1.5 transition-all"
                                title="Remover"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remover indisponibilidade</AlertDialogTitle>
                                <AlertDialogDescription>Tem certeza que deseja remover esta indisponibilidade?</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleRemoveBlock(item.id)} className="bg-red-500 hover:bg-red-600">Remover</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Filter */}
            <div className="flex gap-2">
              {['', 'pendente', 'confirmado', 'recusado'].map(s => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-inter font-semibold transition-all ${
                    filterStatus === s
                      ? 'bg-[#1A4D2E] text-white'
                      : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {s === '' ? 'Todos' : STATUS_CONFIG[s]?.label || s}
                </button>
              ))}
            </div>

            {/* Escalas List by Date */}
            {sortedDates.length === 0 ? (
              <Card className="bg-white rounded-2xl p-8 text-center shadow-md border border-[#E1ECE5]">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="font-inter text-gray-500">Nenhuma escala encontrada para {areaLabels[selectedArea] || selectedArea}</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {sortedDates.map(date => (
                  <Card key={date} className="bg-white rounded-2xl shadow-sm border border-[#E1ECE5] overflow-hidden">
                    <div className="bg-[#F2F7F4] px-4 py-2.5 border-b border-[#E1ECE5]">
                      <p className="font-nunito font-bold text-[#1A4D2E] flex items-center gap-2">
                        <Calendar className="w-4 h-4" /> {formatDate(date)}
                      </p>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {groupedByDate[date].map(e => {
                        const cfg = STATUS_CONFIG[e.status] || STATUS_CONFIG.pendente;
                        const StatusIcon = cfg.icon;
                        const isMine = e.volunteer_id === volunteerId;
                        return (
                          <div key={e.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50/50 transition-colors">
                            <div className="flex items-center gap-3">
                              <StatusIcon className="w-5 h-5" style={{color: e.status === 'confirmado' ? '#16a34a' : e.status === 'recusado' ? '#dc2626' : '#d97706'}} />
                              <div>
                                <p className="font-inter font-semibold text-gray-800 text-sm">
                                  {e.volunteer_name}
                                  {isMine && <span className="text-xs text-[#1A4D2E] ml-1">(voce)</span>}
                                </p>
                                {e.approved_by && (
                                  <p className="font-inter text-xs text-gray-400">
                                    {e.status === 'confirmado' ? 'Aprovado' : 'Recusado'} por {e.approved_by}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.color}`}>
                                {cfg.label}
                              </span>
                              {/* Approval buttons for admin/leaders */}
                              {(isAdmin || isFullAdmin() || liderAreas.includes(selectedArea)) && e.status === 'pendente' && (
                                <div className="flex gap-1 ml-2">
                                  <button
                                    data-testid={`approve-${e.id}`}
                                    onClick={() => handleApprove(e.id, 'confirmado')}
                                    className="bg-green-100 hover:bg-green-200 text-green-700 rounded-full p-1.5 transition-all"
                                    title="Confirmar"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button
                                    data-testid={`reject-${e.id}`}
                                    onClick={() => handleApprove(e.id, 'recusado')}
                                    className="bg-red-100 hover:bg-red-200 text-red-700 rounded-full p-1.5 transition-all"
                                    title="Recusar"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                              {/* Cancel for own pending requests */}
                              {isMine && e.status === 'pendente' && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <button
                                      data-testid={`cancel-${e.id}`}
                                      className="text-gray-400 hover:text-red-500 rounded-full p-1.5 transition-all"
                                      title="Cancelar"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Cancelar escala</AlertDialogTitle>
                                      <AlertDialogDescription>Tem certeza que deseja cancelar esta escala?</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Voltar</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleCancel(e.id)} className="bg-red-500 hover:bg-red-600">Cancelar Escala</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <Navigation active="inicio" />
    </div>
  );
};

export default EscalaTrabalho;
